import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import classNames from "classnames";

import type { RTEComponentDefs, RichTextEditorSerializeAs } from "../RichTextEditorProps";
import { formatSerializedOutput } from "../utils/formatSerializedOutput";
import { highlightSerializedOutput } from "../utils/highlightSerializedOutput";

import CSS from "../styles.module.css";

export interface RTEOutputDefs {
  readonly value: string | null;
  readonly serializeAs: RichTextEditorSerializeAs;
  readonly label?: string;
}

const OUTPUT_LANGUAGE: Map<RichTextEditorSerializeAs, string> = new Map([
  ["html", "jsx"],
  ["components", "json"],
  ["lexical-json", "json"],
  ["markdown", "markdown"],
]);

const OUTPUT_BADGE: Map<RichTextEditorSerializeAs, string> = new Map([
  ["html", "HTML"],
  ["components", "JSON"],
  ["lexical-json", "JSON"],
  ["markdown", "Markdown"],
]);

const COPIED_RESET_MS = 1400;

const legacyCopy = (text: string): boolean => {
  if (typeof document === "undefined") return false;
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
};

export const Output: RTEComponentDefs<RTEOutputDefs> = (props) => {
  const { label = "Serialized output", value = "" } = props;
  const { Output, OutputPre, OutputCode, OutputActions, OutputBadge, OutputCopy, copied: copiedClass } = CSS;
  const lang = OUTPUT_LANGUAGE.get(props.serializeAs);
  const badge = OUTPUT_BADGE.get(props.serializeAs);
  const formatted = value ? formatSerializedOutput(value, props.serializeAs) : "";
  const tokens = useMemo(() => highlightSerializedOutput(formatted, props.serializeAs), [formatted, props.serializeAs]);
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleCopy = useCallback(() => {
    const clipboard = typeof navigator !== "undefined" ? navigator.clipboard : undefined;
    if (clipboard?.writeText) {
      clipboard.writeText(formatted).then(
        () => setCopied(true),
        () => {
          if (legacyCopy(formatted)) setCopied(true);
        },
      );
      return;
    }
    if (legacyCopy(formatted)) setCopied(true);
  }, [formatted]);

  useEffect(() => {
    if (!copied) return undefined;
    resetTimer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    return () => clearTimeout(resetTimer.current);
  }, [copied]);

  return (
    <output className={Output} aria-label={label} data-testid="rich-text-editor-output">
      <div className={OutputActions}>
        {badge != null && <span className={OutputBadge}>{badge}</span>}
        <button
          type="button"
          className={classNames(OutputCopy, { [copiedClass]: copied })}
          onClick={handleCopy}
          aria-label={copied ? "Copied!" : "Copy"}
          aria-live="polite"
          data-testid="rich-text-editor-output-copy"
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre className={OutputPre}>
        <code className={OutputCode} data-language={lang}>
          {tokens.map((token, index) =>
            token.type === "plain" ? (
              <Fragment key={index}>{token.value}</Fragment>
            ) : (
              <span key={index} data-token={token.type}>
                {token.value}
              </span>
            ),
          )}
        </code>
      </pre>
    </output>
  );
};
